import React, { FC, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider';

interface RouteGuardProps {
  children: React.ReactNode;
}

/**
 * RouteGuard component that restricts access based on user roles
 *
 * Rules:
 * - dispatcher: only access to "Planificare Mentenante" (/service-maintenance/dispatch/maintenancePlanning)
 * - senior_tehnician, tehnician: only access to "Mentenantele Mele" (/service-maintenance/dispatch/myMaintenancePlannings)
 *   and "Fise de interventie" (/service-maintenance/interventionFiles)
 * - All other roles: full access
 */
const RouteGuard: FC<RouteGuardProps> = ({ children }) => {
  const { profile } = useAuth();
  const location = useLocation();
  const path = location.pathname;

  const normalizeRole = (role: string) => role.toLowerCase().replace(/_/g, '-').trim();
  const userRoles = useMemo(() => profile?.userPermission?.rols || [], [profile]);
  const normalizedRoles = useMemo(() => new Set(userRoles.map(normalizeRole)), [userRoles]);
  const hasRole = (...rolesToCheck: string[]) => rolesToCheck.some((role) => normalizedRoles.has(normalizeRole(role)));

  // Admin has full access to all routes
  const isAdmin = hasRole('admin');
  const hasCommercialDispatchAccess = hasRole(
    'manager',
    'accountant',
    'pre-sales',
    'sales',
    'senior-sales',
    'senior_sales',
  );

  // Define allowed paths for each restricted role
  const dispatcherAllowedPaths = [
    '/home',
    '/logout',
    '/service-maintenance/dispatch/maintenancePlanning',
    '/service-maintenance/interventionFiles',
    '/service-maintenance/interventionFile/edit', // Allow editing intervention files
    '/service-maintenance/interventionFile/new', // Allow creating from maintenance planning (component will validate)
    '/access-denied',
    '/notifications', // Allow notifications
  ];

  const technicianAllowedPaths = [
    '/home',
    '/logout',
    '/service-maintenance/dispatch/myMaintenancePlannings',
    '/service-maintenance/interventionFiles',
    '/service-maintenance/interventionFile/edit', // Allow editing intervention files
    '/service-maintenance/interventionFile/new', // Allow creating from maintenance planning (component will validate)
    '/access-denied',
  ];

  // Check if user is dispatcher
  const isDispatcher = userRoles.includes('dispatcher') && !isAdmin && !hasCommercialDispatchAccess;

  // Check if user is technician (senior_tehnician or tehnician) but not admin
  const isTechnician =
    (userRoles.includes('senior_tehnician') || userRoles.includes('tehnician')) && !isAdmin && !hasCommercialDispatchAccess;

  // Admin has full access - skip all restrictions
  if (isAdmin) {
    return <>{children}</>;
  }

  // If dispatcher, only allow access to maintenance planning
  if (isDispatcher) {
    const isAllowed = dispatcherAllowedPaths.some((allowedPath) => path.startsWith(allowedPath));
    if (!isAllowed) {
      return <Navigate to='/access-denied' replace />;
    }
  }

  // If technician, only allow access to their maintenances and intervention files
  if (isTechnician) {
    const isAllowed = technicianAllowedPaths.some((allowedPath) => path.startsWith(allowedPath));
    if (!isAllowed) {
      return <Navigate to='/access-denied' replace />;
    }
  }

  return <>{children}</>;
};

export default RouteGuard;
